import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import BuildingComponentModal from '../modals/BuildingComponentModal';
import BuildingComponentContent from './BuildingComponentContent';

const form = {
  keyword: '',
};

function BuildingComponentContainer({
  match,
  history,
  getBuildingComponentTypeList,
  componentsList,
  isLoading,
  removeBuildComponentType,
  componentsListPager,
  getSingleComponentType,
  ordinanceRevisionId,
  clearItem
}) {
  const [formData, setFormData] = useState(form);
  const setModal = useContext(ModalContext);

  const handleOnChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEdit = (data) => (e) => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <BuildingComponentModal
          updateList={data.attributes}
          id={data.id}
          type="EDIT"
          ordinanceRevisionId={ordinanceRevisionId}
        />
      ),
      title: 'Update Building Component',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = (data) => (e) => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove this building component?',
      () => {
        removeBuildComponentType(data.id, () => {
          getBuildingComponentTypeList({ isPaginate: 1, ordinance_revision_id: ordinanceRevisionId });
          const id = _.get(componentsList, '0.id');
          history.push(`${id || ''}`);
          if (id) getSingleComponentType(id);
        });
      },
      () => { },
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  useEffect(() => {
    getBuildingComponentTypeList(
      {
        isPaginate: 1,
        ordinance_revision_id: ordinanceRevisionId,
      },
      (res) => {
        const id = _.get(res, 'data.0.id');
        if (id) {
          history.push(`/rpt/building/settings/building-components/${id}`);
          getSingleComponentType(id);
        } else clearItem(c.GET_SINGLE_BUILDING_COMPONENT_TYPE, {});
      }
    );
    return () => {
      setModal({ isOpen: false });
    };
  }, [ordinanceRevisionId]);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    q: formData.keyword,
    sort_by: 'asc',
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      getBuildingComponentTypeList({ isPaginate: 1, q: formData.keyword, ordinance_revision_id: ordinanceRevisionId });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm]);

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:componentId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Building Components"
            getData={getBuildingComponentTypeList}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search..."
            pathName={`${match.path}/:componentId?`}
            listName="classificationCodes"
            label="Building Components"
            renderItem={(item) => (
              <div className="p-3">{_.get(item, 'attributes.name')}</div>
            )}
            noRightCard
            isLoading={isLoading}
            selected={params.componentId}
            data={componentsList}
            dataPager={componentsListPager}
            handleSelectRow={(data) => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:componentId?`}
                    render={() => (
                      <BuildingComponentContent
                        handleEdit={handleEdit}
                        handleRemove={handleRemove}
                        ordinanceRevisionId={ordinanceRevisionId}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:componentId?`}
                  render={() =>
                    !params.componentId && componentsList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(componentsList, '0.id')}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

BuildingComponentContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getBuildingComponentTypeList: PropTypes.instanceOf(Function).isRequired,
  componentsList: PropTypes.instanceOf(Array).isRequired,
  componentsListPager: PropTypes.instanceOf(Object).isRequired,
  removeBuildComponentType: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getSingleComponentType: PropTypes.instanceOf(Function).isRequired,
  ordinanceRevisionId: PropTypes.number.isRequired,
  clearItem: PropTypes.instanceOf(Function).isRequired,
};

const mapStateToProps = ({ api }) => ({
  componentsList: _.get(api, `${c.GET_BUILDING_COMPONENT_TYPE}.list`) || [],
  componentsListPager:
    _.get(api, `${c.GET_BUILDING_COMPONENT_TYPE}.pager`) || {},
  isLoading:
    _.get(api, 'loading', []).indexOf(c.GET_BUILDING_COMPONENT_TYPE) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(BuildingComponentContainer);
